import React from 'react';
import { Box, Typography, Grid, useTheme, alpha, Container, Card } from '@mui/material';
import { StyledTextField } from './index';
import { usePermissions } from '../../../utils/permissions';
import { Person as PersonIcon } from '@mui/icons-material';

const PrimaryContact = ({ 
  companyInfo, 
  handleInputChange, 
  loading, 
  requiredFields, 
  validationErrors 
}) => {
  const theme = useTheme();
  const { hasPermission, canAccessPage, canEditPage } = usePermissions();
  
  // Permission check for primary contact access
  if (!canAccessPage('primary_contact')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Primary Contact Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access the Primary Contact settings. Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  const isFieldRequired = (fieldName) => {
    return requiredFields['primary-contact']?.includes(fieldName) || false;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const canEditSettings = canEditPage('primary_contact');

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Primary Contact Person
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Designate the main point of contact for your organization
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Contact Name" 
            name="contact_person_name"
            value={companyInfo.contact_person_name} 
            onChange={handleInputChange} 
            required={isFieldRequired('contact_person_name')}
            error={!!getFieldError('contact_person_name')}
            helperText={getFieldError('contact_person_name')}
            disabled={loading || !canEditSettings}
            placeholder="John Doe"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Title/Position" 
            name="contact_person_title"
            value={companyInfo.contact_person_title} 
            onChange={handleInputChange} 
            required={isFieldRequired('contact_person_title')}
            error={!!getFieldError('contact_person_title')}
            helperText={getFieldError('contact_person_title')}
            disabled={loading || !canEditSettings}
            placeholder="CEO"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Contact Phone" 
            name="contact_person_phone"
            value={companyInfo.contact_person_phone} 
            onChange={handleInputChange} 
            required={isFieldRequired('contact_person_phone')}
            error={!!getFieldError('contact_person_phone')}
            helperText={getFieldError('contact_person_phone')}
            disabled={loading || !canEditSettings}
            placeholder="(123) 456-7890"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Contact Email" 
            name="contact_person_email"
            type="email"
            value={companyInfo.contact_person_email} 
            onChange={handleInputChange} 
            required={isFieldRequired('contact_person_email')}
            error={!!getFieldError('contact_person_email')}
            helperText={getFieldError('contact_person_email')}
            disabled={loading || !canEditSettings}
            placeholder="john@yourcompany.com"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PrimaryContact;